import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { UserInfo } from "../../../shared/types/user-info";
import { configurableOptionsTypes, getConfigurableOptionSearchFilterOptions } from "./configurable-options";
import { ConfigurableOptionService } from "./configurable-options.service";
import { ConfigurableOption } from "./configurable-options.type";

@Injectable({
  providedIn: "root",
})
export class ConfigurableOptionTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private configurableOptionService: ConfigurableOptionService,
    private dataTableService: DataTableService<ConfigurableOption>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getConfigurableOptionTableConfig = (config?: DataTableConfig): DataTable<ConfigurableOption> => {
    const { hidden, shown, actionColumn } = config || {};

    const columns: DataTableColumn<ConfigurableOption>[] = (
      [
        {
          prop: "type",
          name: "Type",
          inlineEdit: {
            control: "select",
            select: {
              options: configurableOptionsTypes,
            },
          },
          render: (configurableOption) =>
            configurableOptionsTypes.find((type) => type.value === configurableOption.type)?.label,
        },
        {
          prop: "value",
          name: "Value",
          link: {
            path: (configurableOption) => `/configurable-options/${configurableOption.id}`,
          },
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255)],
          },
        },
      ] as DataTableColumn<ConfigurableOption>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (configurableOption) => col?.inlineEdit?.disabled?.(configurableOption),
        },
      }));

    return {
      id: "co",
      entityName: "Configurable Option",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "configurable-options",
          output: "browser",
          data: {
            browser: (queryParams) =>
              this.configurableOptionService.getConfigurableOptions({
                ...queryParams,
                where: {
                  ...queryParams?.where,
                },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewConfigurableOption(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageConfigurableOption,
              hidden: (configurableOption) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteConfigurableOption,
              hidden: (configurableOption) => actionColumn?.delete?.hidden || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getConfigurableOptionSearchFilterOptions(this.currentUser),
        },
      ],
    };
  };

  /** Populate table */
  getConfigurableOptions = (event: GetDataEvent<ConfigurableOption>): void => {
    const { onError, onResult, queryParams } = event;
    this.loader.show();
    this.configurableOptionService
      .getConfigurableOptions({
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data) => {
          onResult(data);
          this.loader.hide();
        },
        (error) => {
          onError(error);
          this.loader.hide();
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<ConfigurableOption>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.configurableOptionService.updateConfigurableOption(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewConfigurableOption = (configurableOption: ConfigurableOption, config?: DataTableConfig): void => {
    const columns = this.getConfigurableOptionTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(configurableOption, col, get(configurableOption, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageConfigurableOption = (configurableOption: ConfigurableOption): void => {
    this.router.navigate([`/configurable-options/${configurableOption.id}`]);
  };

  private deleteConfigurableOption = (
    configurableOption: ConfigurableOption,
    table: DataTableComponent<ConfigurableOption>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Configurable Option",
        data: configurableOption,
        url: this.configurableOptionService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  //#endregion
}
